import React, { useContext, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { ArrowCircleLeft, Hand, Pencil, Trash } from "phosphor-react";
import { transformDataToMapboxSource } from "utils";
import { UserContext } from "context/user";
import { MapActions, MapContext } from "context/map";
import { deleteField } from "api/Field";
import Confirm from "components/Confirm";
import "./index.scss";

const ManageToolbar = () => {
  const location = useLocation();
  const { token } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const {
    map,
    farm,
    fields,
    draw,
    manageStatus,
    selectedFields,
    currentField,
  } = state;

  const drawField = useCallback(
    (newFields) => {
      const transformedFields = transformDataToMapboxSource(newFields);

      transformedFields.forEach((transformedField) => {
        const { id } = transformedField.data;

        if (!map.getSource(`source-${id}`)) {
          map.addSource(`source-${id}`, transformedField);
        }

        if (!map.getLayer(`outline-${id}`)) {
          map.addLayer({
            id: `outline-${id}`,
            type: "line",
            source: `source-${id}`,
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                "#09BA51",
                "#FFFFFF",
              ],
              "line-width": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                2,
                3,
              ],
              "line-opacity": 0.7,
            },
          });
        }

        if (!map.getLayer(`fill-${id}`)) {
          map.addLayer({
            id: `fill-${id}`,
            type: "fill",
            source: `source-${id}`,
            paint: {
              "fill-color": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                "#09BA51",
                "#FFFFFF",
              ],
              "fill-opacity": [
                "case",
                ["boolean", ["feature-state", "tiled"], false],
                0,
                ["boolean", ["feature-state", "active"], false],
                0.2,
                ["boolean", ["feature-state", "hover"], false],
                0.5,
                0,
              ],
            },
          });
        }
      });
    },
    [map]
  );

  const setViewMode = () => {
    if (selectedFields.length) {
      draw.delete(selectedFields[0]);

      const filteredFields = fields.filter(
        (item) => item.id === selectedFields[0]
      );

      drawField(filteredFields);
    }

    if (
      manageStatus === "create-boundaries" ||
      manageStatus === "create" ||
      manageStatus === "edit"
    ) {
      if (currentField) {
        draw.delete(currentField);
      }
      draw.trash();
      draw.changeMode("static");
    }

    dispatch({ type: MapActions.SET_VIEW_MODE });
  };

  const navigateFarm = () => {
    if (!manageStatus) return;

    if (selectedFields.length) {
      draw.delete(selectedFields[0]);

      const filteredFields = fields.filter(
        (item) => item.id === selectedFields[0]
      );

      drawField(filteredFields);
    }

    if (manageStatus === "create-boundaries" || manageStatus === "create") {
      if (currentField) {
        draw.delete(currentField);
      }
      draw.trash();
      draw.changeMode("static");
    }

    dispatch({ type: MapActions.SET_NAVIGATE_FARM });
  };

  const createField = useCallback(() => {
    if (["create", "create-boundaries"].includes(manageStatus)) return;

    dispatch({ type: MapActions.SET_CREATE_BOUNDARY });

    draw.changeMode("draw_polygon");
  }, [dispatch, draw, manageStatus]);

  const removeField = async () => {
    try {
      await deleteField(selectedFields[0], token);

      draw.delete(selectedFields[0]);

      dispatch({
        type: MapActions.REMOVE_FIELD,
        value: selectedFields[0],
      });

      toast.success("Field deleted successfully");
    } catch {
      toast.error("Can't delete field!");
    }
  };

  const removeFieldConfirmation = () => {
    if (!selectedFields.length) {
      toast.error("Select a field to delete!");

      return;
    }

    const options = {
      title: "Are you sure?",
      message: "Deleting this field will removed all associated layers.",
      buttons: [
        {
          label: "Yes, delete anyway.",
          onClick: () => {
            removeField();
          },
          className: "-danger",
        },
        {
          label: "No",
          onClick: () => {},
          className: "",
        },
      ],
      childrenElement: () => <div />,
      customUI: ({ title, message, buttons, onClose }) => (
        <Confirm
          title={title}
          message={message}
          buttons={buttons}
          onClose={onClose}
        />
      ),
      closeOnEscape: false,
      closeOnClickOutside: false,
      overlayClassName: "overlay-custom-class-name",
    };

    confirmAlert(options);
  };

  useEffect(() => {
    if (location && location.state?.draw && draw) {
      createField();
    }
  }, [createField, draw, location]);

  const renderFarmInformation = () => {
    return (
      <section className="farm-information">
        <button
          type="button"
          className="action-button"
          onClick={() => {
            setViewMode();
          }}
        >
          <ArrowCircleLeft size={20} color="#ffffff" />
        </button>
        <p className="farm-name">{farm.properties.farm_name}</p>
      </section>
    );
  };

  const renderActions = () => {
    return (
      <section className="action-wrapper">
        <div className="action-items">
          <p className="action-title">Boundaries</p>
          <div className="icons-wrapper">
            <button
              type="button"
              className={`action-button ${
                manageStatus === null ? "-active" : ""
              }`}
              onClick={() => {
                navigateFarm();
              }}
            >
              <Hand size={20} color="#ffffff" />
            </button>
            <button
              type="button"
              className={`action-button ${
                ["create", "create-boundaries"].includes(manageStatus)
                  ? "-active"
                  : ""
              }`}
              onClick={() => {
                if (!manageStatus) {
                  createField();
                }
              }}
              disabled={!!manageStatus}
            >
              <Pencil size={20} color="#ffffff" />
            </button>
            <button
              type="button"
              className={`action-button ${
                !selectedFields.length ? "-disabled" : "-active"
              }`}
              onClick={() => {
                removeFieldConfirmation();
              }}
              disabled={!selectedFields.length}
            >
              <Trash
                size={20}
                color={!selectedFields.length ? "gray" : "#ffffff"}
              />
            </button>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="manage-tool-wrapper">
      <div className="draw-tools">
        {renderFarmInformation()}
        {renderActions()}
      </div>
    </div>
  );
};

export default ManageToolbar;
